import React from 'react';
import { Link } from 'react-router-dom';
import './FormPage.css';

const ApiIntegrationPage = () => {
  return (
    <div className="form-page">
      <h1>API Integration</h1>
      
      <div className="content-section">
        <h2>Integrating with External Sites</h2>
        <p>
          BoilerWorks TypeForms can be easily integrated with any website or application.
          Simply make a POST request to our API endpoint with your form data.
        </p>
        
        <h3>Example API Integration</h3>
        <p>
          Use this code snippet to integrate with any website, including Hugo, WordPress, or any custom site:
        </p>
        
        <div className="code-example">
          <pre>
            <code>{`
// Form submission handler
async function submitFormToTypeforms(formType, formData) {
  const endpoint = "https://forms.conflict.media/api/forms/submit";
  
  try {
    const response = await fetch(endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        formType: formType,
        formData: formData
      })
    });
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.message || "Form submission failed");
    }
    
    return result;
  } catch (error) {
    console.error("Form submission error:", error);
    throw error;
  }
}

// Example usage with form submit event
document.querySelector('#contact-form').addEventListener('submit', async (event) => {
  event.preventDefault();
  
  // Get form data
  const formData = {
    name: document.querySelector('#name').value,
    email: document.querySelector('#email').value,
    message: document.querySelector('#message').value
  };
  
  try {
    // Submit form to TypeForms backend
    const result = await submitFormToTypeforms('contact', formData);
    console.log("Form submitted successfully:", result);
    // Show success message to user
  } catch (error) {
    console.error("Error submitting form:", error);
    // Show error message to user
  }
});`}</code>
          </pre>
        </div>
        
        <h3>Complete Hugo Implementation</h3>
        <p>
          For a complete Hugo site integration example, see our <a href="/docs/integration-examples.html">Integration Examples</a> documentation.
        </p>
      </div>
      
      <div className="content-section">
        <h2>API Reference</h2>
        
        <h3>Endpoint</h3>
        <p>
          <code>POST https://forms.conflict.media/api/forms/submit</code>
        </p>
        
        <h3>Request Format</h3>
        <div className="code-example">
          <pre>
            <code>{`
{
  "formType": "string",  // Type of form (e.g., "contact", "registration")
  "formData": {
    // Form field values as key-value pairs
    "field1": "value1",
    "field2": "value2",
    // ...
  }
}`}</code>
          </pre>
        </div>
        
        <h3>Response Format</h3>
        <div className="code-example">
          <pre>
            <code>{`
{
  "success": true,
  "message": "Form submitted successfully",
  "id": "form_contact_1234567890123",
  "emailSent": true,
  "timestamp": "2023-01-01T00:00:00.000Z"
}`}</code>
          </pre>
        </div>
        
        <h3>Error Response</h3>
        <div className="code-example">
          <pre>
            <code>{`
{
  "success": false,
  "message": "Error message describing what went wrong"
}`}</code>
          </pre>
        </div>
        
        <h3>Testing</h3>
        <p>
          To test your integration without sending real emails, add a test keyword in your form data:
        </p>
        <div className="code-example">
          <pre>
            <code>{`
// This will be processed but won't send email notifications
const testFormData = {
  name: "Test User",
  email: "test@example.com",
  message: "Testing integration"
};`}</code>
          </pre>
        </div>
      </div>
      
      <div className="api-example">
        <div className="api-section">
          <h3>Basic Integration</h3>
          <p>To submit form data to your API, provide an onSubmit handler function to the Form component:</p>
          <pre>
{`import { Form } from 'boilerworks-typeforms';

const MyForm = () => {
  const handleSubmit = async (formValues, csrfToken) => {
    try {
      const response = await fetch('https://your-api.com/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken // If CSRF protection is enabled
        },
        body: JSON.stringify({
          formData: formValues,
          timestamp: new Date().toISOString()
        })
      });
      
      if (!response.ok) {
        throw new Error('Submission failed');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Error submitting form:', error);
      throw error;
    }
  };
  
  return (
    <Form
      formData={myFormData}
      onSubmit={handleSubmit}
    />
  );
};`}
          </pre>
        </div>
        
        <div className="api-section">
          <h3>Using the API Utility</h3>
          <p>For convenience, you can use the included API utility to handle form submissions:</p>
          <pre>
{`import { Form } from 'boilerworks-typeforms';
import { submitForm } from './utils/api';

const MyForm = () => {
  const handleSubmit = async (formValues, csrfToken) => {
    return submitForm('contact', formValues, csrfToken);
  };
  
  return (
    <Form
      formData={myFormData}
      onSubmit={handleSubmit}
    />
  );
};`}
          </pre>
        </div>
        
        <div className="api-section">
          <h3>API Response Format</h3>
          <p>Your API should return a response in the following format:</p>
          <pre>
{`{
  "success": true,
  "message": "Form submitted successfully",
  "id": "SUBMISSION-123", // Optional reference ID
  "timestamp": "2023-11-15T12:34:56Z" // ISO timestamp
}`}
          </pre>
          <p>If there's an error, the API should return:</p>
          <pre>
{`{
  "success": false,
  "message": "Error message explaining what went wrong",
  "errors": { // Optional field-specific errors
    "email": "Invalid email format",
    "phone": "Phone number is required"
  }
}`}
          </pre>
        </div>
        
        <div className="api-section">
          <h3>Cloudflare Worker Integration</h3>
          <p>If you're using Cloudflare Workers, check out the example form submission handler:</p>
          <pre>
{`// Cloudflare Worker example
addEventListener('fetch', event => {
  event.respondWith(handleRequest(event.request))
})

async function handleRequest(request) {
  // Handle OPTIONS for CORS preflight
  if (request.method === 'OPTIONS') {
    return handleCors();
  }
  
  // Handle form submission
  if (request.method === 'POST' && 
      request.url.includes('/api/forms/submit')) {
    try {
      const formData = await request.json();
      
      // Store data in KV
      await FORMS_KV.put(
        \`form_\${formData.formType}_\${Date.now()}\`,
        JSON.stringify(formData)
      );
      
      // You can also send notifications via email
      // using the Email API
      
      return new Response(
        JSON.stringify({
          success: true,
          message: 'Form submitted successfully',
          id: \`REF-\${Math.floor(Math.random() * 10000)}\`,
          timestamp: new Date().toISOString()
        }),
        {
          headers: {
            'Content-Type': 'application/json',
            ...corsHeaders
          }
        }
      );
    } catch (error) {
      return new Response(
        JSON.stringify({
          success: false,
          message: 'Error processing form submission'
        }),
        {
          status: 500,
          headers: {
            'Content-Type': 'application/json',
            ...corsHeaders
          }
        }
      );
    }
  }
}`}
          </pre>
        </div>
      </div>
      
      <p>For practical examples of form integration, check out:</p>
      <ul>
        <li><Link to="/contact-form">Contact Form Example</Link></li>
        <li><Link to="/survey-form">Survey Form Example</Link></li>
        <li><Link to="/registration-form">Registration Form Example</Link></li>
      </ul>
    </div>
  );
};

export default ApiIntegrationPage; 